import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { EventType, type AuthenticationResult } from '@azure/msal-browser';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import App from './App';
import { msalInstance } from '@/auth/msalConfig';
import { ColorModeProvider } from '@/theme/ColorModeContext';
import { RequireAuth } from '@/auth/RequireAuth';
import '@/i18n';

// Ensure an active account is set after redirect sign-in.
if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
  msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
}

// Keep the active account in sync with whichever account just completed
// interactive sign-in (covers popup/redirect flows triggered later, e.g. re-auth).
msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const payload = event.payload as AuthenticationResult;
    msalInstance.setActiveAccount(payload.account);
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

msalInstance.initialize().then(() => {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <ColorModeProvider>
          <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
            <QueryClientProvider client={queryClient}>
              <BrowserRouter>
                <RequireAuth>
                  <App />
                </RequireAuth>
              </BrowserRouter>
            </QueryClientProvider>
          </SnackbarProvider>
        </ColorModeProvider>
      </MsalProvider>
    </React.StrictMode>,
  );
});
